/**
 * VRG-549 — "what does this control edit?", the app half.
 *
 * Studio pushes `{ type: 'studio-highlight', token }` down the same postMessage bridge it
 * already uses for the theme. This module answers it: it outlines the elements the named
 * Studio token is CURRENTLY PAINTING on the flow in view, and reports back a labelled
 * count that Studio shows beside the control.
 *
 * ## The mechanism is a DIFFERENTIAL, and that is the whole design
 *
 * Membership is decided by changing the token and watching what moves. The module renders
 * the flow twice more with the token perturbed, snapshots the computed styles each time,
 * and takes the elements whose styles differ. No selector decides who is in the set.
 *
 * This is the third shape of this code and the reasoning for each step is worth keeping,
 * because both earlier shapes were defensible and both were wrong:
 *
 * 1. **A selector list per token.** "Primary colour means headings and contained buttons."
 *    Measured on the certificate flow with `primary_color` moved #C8102E -> #00A000, it
 *    outlined 16 elements of which exactly ONE changed colour. Ten of the "headings" were
 *    `MuiTypography-h3` whose call sites override the theme back to black. A panel reading
 *    "Headings (11)" beside a preview where one heading moves is the false confidence this
 *    ticket exists to remove.
 *
 * 2. **Selector as CANDIDATE, verified against the token's live value.** This fixed (1)'s
 *    false positives and shipped. It has two failures that only a measurement finds, and
 *    Spencer found them by using it: *"That mapping is missing a lot of spotlights on the
 *    app preview pages... Fix for radius, secondary."*
 *    - The list drifts. Hand-authored candidates cannot keep up with the code they
 *      describe, and there is nothing that fails when they fall behind.
 *    - An EXACT-VALUE test rejects genuine hits. A `border_radius` of 20 renders 6px on a
 *      field (`fieldMetrics.ts` clamps it) and 8px on an `sx={{ borderRadius: 1 }}` box;
 *      both ARE the token, neither EQUALS it. Measured: 23 of 43 affected elements
 *      outlined. The residue was not an edge case, it was half the answer.
 *
 * 3. **This.** "Does it change when the token changes" needs no list and admits no
 *    threshold, so neither failure is available to it. A clamped field moves when the
 *    token moves and is therefore in; a hardcoded `#000000` heading does not move and is
 *    therefore out — including in the case that defeats every value-matching scheme, where
 *    the hardcoded value happens to EQUAL the token (the default theme makes
 *    `primary_color` and `text_color` both `#000000`, so that is the common case, not an
 *    edge one).
 *
 * The tempting middle road — keep the gate but loosen it to "any plausible multiple, any
 * near colour" — is the one thing not to do. It buys the missing 20 by readmitting the 15
 * that shape (1) got wrong, and a highlight that is generous is back to asserting.
 *
 * ## Why it does not flicker — and the half of that claim which was wrong
 *
 * The probe renders are driven through `flushSync`, so the perturbed tree exists only
 * within one JavaScript task. The browser paints between tasks, never inside one, so the
 * sentinel colours are measured and reverted without ever reaching the screen. Take the
 * `flushSync` away and the operator watches their preview flash black then white.
 *
 * That reasoning is correct and it was not SUFFICIENT, which Spencer found by using it:
 * *"this photo buttons flashes on the preview back and forth when I click on any 'What
 * does this change?'"*
 *
 * The perturbed tree is never painted. The CSS TRANSITIONS it arms are, afterwards. A
 * transition is created during a style recalc whose computed value differs from the
 * previous one — and `snapshot()` forces exactly that recalc, on every element, twice per
 * probe. `flushSync` bounds the task; it does nothing about time-based animations that the
 * task started and that play out over the frames AFTER it ends. So the operator watches the
 * probe value animate in and back out over the following 150–500ms.
 *
 * MEASURED on the certificate flow rather than reasoned about: of 2,453 elements, 67 carry
 * a live transition and 48 of those are visible AND transition a property the probe moves —
 * `MuiIconButton-sizeSmall` at `background-color 0.15s` and `MuiSvgIcon` at `fill 0.2s` are
 * the photo controls Spencer named, with `MuiButton-contained` at 0.25s and `MuiSwitch-track`
 * at 0.5s behind them. Two competing explanations were ruled out in the same pass: no image
 * `src` changes across a probe (9 before, 9 after, none different) and no component
 * remounts (the only nodes added are emotion's own `<style>` elements), so neither an image
 * reload nor a carousel re-init is involved.
 *
 * `cancelArmedTransitions` is the fix, and it is aimed at the mechanism rather than at the
 * symptom. Two shapes that look right and are not, both ruled out by measurement rather than
 * by argument:
 *
 * - **Hiding the preview for the probe window.** The transitions are armed regardless and
 *   would simply play once the preview came back, so this hides the cause of the flash
 *   without removing the flash.
 * - **Holding `transition: none` across the whole probe.** This kills the flash AND moves the
 *   numbers: `text_color` on the certificates flow outlined 13 elements unfrozen and 29
 *   frozen, at a fixed tree, and all 16 extra were absent from a real-render gold standard.
 *   A feature built to stop this panel asserting things it has not verified cannot ship a
 *   fix that invents sixteen of them.
 *
 * Cancelling after the fact has neither problem. See that function for why one synchronous
 * task is what makes it sufficient.
 *
 * Verified rather than assumed: with a stable per-element key, every monitored property of
 * every element reads identically before and after a probe — 0 of 385 drifted. (An earlier
 * check compared two snapshots by ARRAY INDEX and reported ~300 drifted; the tree grows as
 * drawers mount, so the indices had slid. Worth recording because the false alarm was more
 * convincing than the truth.)
 *
 * One thing the synchronous probe demands in exchange, and it is not optional: the baseline
 * must be measured after a synchronous render too. See `measureTokenHighlight`.
 *
 * ## What the differential still cannot tell you, and who says so
 *
 * It measures the DOM in front of it. The Studio preview renders four of the app's flows,
 * so whatever is outlined is a slice; `primary_color` alone is read in ~100 places across
 * every screen. Studio's panel is responsible for saying so, and nothing here may be read
 * as "the blast radius" — only as "the part of it visible from here, right now".
 */

/** The seven theme tokens Studio exposes. */
export type StudioTokenName =
  | 'primary_color'
  | 'secondary_color'
  | 'text_color'
  | 'bg_color'
  | 'title_font'
  | 'font'
  | 'border_radius';

/** The Studio theme, as it arrives over the bridge. Only the fields read here. */
export interface HighlightTheme {
  primary_color?: string;
  secondary_color?: string;
  text_color?: string;
  bg_color?: string;
  title_font?: string;
  font?: string;
  border_radius?: string | number;
}

/** A group of affected elements, in operator vocabulary, with how many there are. */
export interface HighlightPartCount {
  label: string;
  count: number;
}

/**
 * Everything the probe reads back, including the parts of the measurement that are NOT
 * outlined — see `invisible`.
 */
export interface HighlightMeasurement {
  parts: HighlightPartCount[];
  /**
   * Elements that provably changed but paint nothing the operator could see: a wrapper
   * whose inherited `color` moved though it renders no text, a transparent `Box` whose
   * corner radius moved though it has no background or border to show a corner with.
   *
   * Reported rather than silently dropped. It is the one number that says how much of the
   * differential the visibility filter is holding back, so a filter that starts eating
   * real hits shows up as a rising count instead of as a quietly shrinking highlight.
   */
  invisible: number;
}

/* ────────────────────────────────────────────────────────────────────────────
   What "moved" means, per token
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * Colour lands in more places than `color` and `background-color`.
 *
 * `backgroundImage` is here because gradients carry colour and because MUI paints some
 * states with one; `boxShadow` because focus rings and the brand glow live there;
 * `fill`/`stroke` because icons are SVG and an icon tinted by the brand is exactly the
 * sort of reach an operator does not expect.
 */
const COLOR_PROPS = [
  'color',
  'backgroundColor',
  'backgroundImage',
  'borderTopColor',
  'borderRightColor',
  'borderBottomColor',
  'borderLeftColor',
  'outlineColor',
  'boxShadow',
  'fill',
  'stroke',
  'textDecorationColor',
] as const;

const RADIUS_PROPS = [
  'borderTopLeftRadius',
  'borderTopRightRadius',
  'borderBottomLeftRadius',
  'borderBottomRightRadius',
] as const;

const FONT_PROPS = ['fontFamily'] as const;

const propsFor = (token: StudioTokenName): readonly string[] => {
  if (token === 'border_radius') return RADIUS_PROPS;
  if (token === 'font' || token === 'title_font') return FONT_PROPS;
  return COLOR_PROPS;
};

/* ────────────────────────────────────────────────────────────────────────────
   The perturbations
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * Two probe values per token, unioned.
 *
 * One is not enough, and the reason is specific rather than cautious:
 *
 * - **Colour** — several derived values are THRESHOLD functions of the token, not
 *   continuous ones. A button's label colour is chosen by the luma of its fill
 *   (`base-email.tsx` does this explicitly; MUI's `contrastText` does it implicitly), so a
 *   probe that stays on the same side of the threshold moves the fill and leaves the label
 *   alone, and the label is genuinely token-driven. The pair straddles it: one near-black,
 *   one near-white. See the case body for the measurement that forced those two values.
 * - **Radius** — the app CLAMPS. `FIELD_RADIUS_MAX` holds fields at 6px, so a token of 20
 *   probed at 37 renders 6px both times and the field reads as unaffected. Probing at 0
 *   passes under every clamp in the app and catches them all; probing upward catches
 *   everything the clamps do not bind.
 * - **Font** — one probe suffices. There is no threshold and no clamp; a family either
 *   comes from the token or it does not.
 */
const probeThemes = (
  token: StudioTokenName,
  theme: HighlightTheme,
): HighlightTheme[] => {
  switch (token) {
    case 'primary_color':
    case 'secondary_color':
    case 'text_color':
    case 'bg_color': {
      /*
        NEAR-BLACK and NEAR-WHITE, and the pair has to straddle the luma threshold rather
        than merely differ.

        A first version probed mid-magenta and near-black, which differ plainly to the eye
        and are on the SAME side of every contrast test in the app. Measured on the
        Claim-ownership flow: an icon tinted through `getContrastText` computed black under
        both, so the differential concluded `primary_color` does not reach it — while a
        real #C8102E -> #FAFAFA render flips it. Two probes only buy anything if they
        disagree about the questions the app actually asks of the value, and the question
        MUI asks most often is "is this light or dark".

        Any element driven DIRECTLY by the token also differs between these two, so the
        pair covers the continuous case as well.
      */
      const current = String(theme[token] ?? '').toLowerCase();
      const dark = current === '#0b0b0b' ? '#121212' : '#0b0b0b';
      const light = current === '#fafafa' ? '#f2f2f2' : '#fafafa';
      return [
        { ...theme, [token]: dark },
        { ...theme, [token]: light },
      ];
    }

    case 'border_radius': {
      const raw = Number(theme.border_radius);
      const base = Number.isFinite(raw) ? raw : 0;
      return [
        { ...theme, border_radius: 0 },
        { ...theme, border_radius: base + 17 },
      ];
    }

    case 'font':
    case 'title_font':
      // A family nothing else in the app names, so a match cannot be a coincidence. It
      // never renders — the probe tree is measured and discarded inside one task.
      return [{ ...theme, [token]: '"VeraTokenProbe", serif' }];

    default:
      return [];
  }
};

/* ────────────────────────────────────────────────────────────────────────────
   Snapshot and diff
   ──────────────────────────────────────────────────────────────────────────── */

const scopeOf = (root: ShadowRoot | null): ParentNode => root ?? document;

const allElements = (root: ShadowRoot | null): Element[] =>
  Array.from(scopeOf(root).querySelectorAll('*'));

/** Every watched property of every element, as one comparable string per element. */
const snapshot = (
  elements: Element[],
  props: readonly string[],
): Map<Element, string[]> => {
  const out = new Map<Element, string[]>();
  for (const el of elements) {
    const computed = window.getComputedStyle(el) as unknown as Record<string, string>;
    out.set(
      el,
      props.map(p => computed[p] ?? ''),
    );
  }
  return out;
};

/* ────────────────────────────────────────────────────────────────────────────
   The visibility filter
   ──────────────────────────────────────────────────────────────────────────── */

const isTransparent = (colour: string): boolean =>
  colour === 'transparent' ||
  colour === 'rgba(0, 0, 0, 0)' ||
  /,\s*0\)$/.test(colour);

/** Does this element render text of its own, as opposed to inheriting a colour it never paints? */
const paintsText = (el: Element): boolean => {
  for (const node of Array.from(el.childNodes)) {
    if (node.nodeType === Node.TEXT_NODE && (node.textContent ?? '').trim() !== '') {
      return true;
    }
  }
  return false;
};

/**
 * Would the operator SEE this element's change?
 *
 * The differential is exact about which elements the token reaches and says nothing about
 * whether the reach is visible, and the gap between those two is not small. `color` and
 * `font-family` are INHERITED, so changing `text_color` moves the computed `color` of
 * every container in the tree — measured at 268 elements against 52 that actually paint a
 * glyph. Outlining all 268 would be a highlight of the DOM rather than of the page, and
 * the wrappers are nested inside the text they contain, so the marks would stack five deep
 * around every sentence.
 *
 * This is a VISIBILITY test, not a value test. That distinction is the one that matters:
 * it can never reject an element for having the "wrong" value, which is how the previous
 * shape lost half of `border_radius`. Everything it does reject is counted and reported
 * (`HighlightMeasurement.invisible`).
 */
const paintsChange = (el: Element, changed: Set<string>): boolean => {
  const computed = window.getComputedStyle(el);

  // A zero-area element paints nothing at all, whatever its styles say.
  const rect = (el as HTMLElement).getBoundingClientRect?.();
  if (rect && rect.width === 0 && rect.height === 0) return false;
  if (computed.visibility === 'hidden' || computed.display === 'none') return false;

  if (changed.has('backgroundColor') && !isTransparent(computed.backgroundColor)) return true;
  if (changed.has('backgroundImage') && computed.backgroundImage !== 'none') return true;
  if (changed.has('boxShadow') && computed.boxShadow !== 'none') return true;
  if (changed.has('fill') && !isTransparent(computed.fill ?? '')) return true;
  if (changed.has('stroke') && !isTransparent(computed.stroke ?? '')) return true;

  // A border colour is only visible through a border that has width and a style.
  const sides = ['Top', 'Right', 'Bottom', 'Left'] as const;
  for (const side of sides) {
    if (!changed.has(`border${side}Color`)) continue;
    const width = parseFloat(
      (computed as unknown as Record<string, string>)[`border${side}Width`] ?? '0',
    );
    const style = (computed as unknown as Record<string, string>)[`border${side}Style`];
    if (width > 0 && style !== 'none') return true;
  }

  if (changed.has('outlineColor')) {
    if (computed.outlineStyle !== 'none' && parseFloat(computed.outlineWidth) > 0) return true;
  }

  // A corner is only visible on something that draws a box: a fill, a border, a shadow, or
  // a clip that cuts its own children to that corner.
  if (RADIUS_PROPS.some(p => changed.has(p))) {
    const hasFill = !isTransparent(computed.backgroundColor) || computed.backgroundImage !== 'none';
    const hasBorder = sides.some(
      s =>
        parseFloat(
          (computed as unknown as Record<string, string>)[`border${s}Width`] ?? '0',
        ) > 0 &&
        (computed as unknown as Record<string, string>)[`border${s}Style`] !== 'none',
    );
    const clips = computed.overflow !== 'visible';
    if (hasFill || hasBorder || clips || computed.boxShadow !== 'none') return true;
  }

  /*
    An icon is painted by `color`, through `fill: currentColor`, and has no text node —
    so the glyph test below rejects it and the fill test above can miss it too, because
    an inherited `currentColor` fill sometimes reports as a `color` change alone.

    Caught by the differential sweep: `primary_color` on the Claim-ownership flow moved a
    `MuiSvgIcon` and its `path`, and both were dropped as invisible. An icon in the brand
    colour is one of the more visible things a brand colour does, so this is not a corner.
  */
  if (changed.has('color') || changed.has('fill') || changed.has('stroke')) {
    const svgish =
      el instanceof SVGElement ||
      /^(svg|path|circle|rect|ellipse|polygon|polyline|line|g|use)$/i.test(el.tagName);
    if (svgish) {
      const fill = (computed as unknown as Record<string, string>).fill ?? '';
      const stroke = (computed as unknown as Record<string, string>).stroke ?? '';
      if (!isTransparent(fill) || !isTransparent(stroke)) return true;
    }
  }

  // Inherited, so last and gated on actually rendering a glyph.
  if (
    (changed.has('color') || changed.has('textDecorationColor') || changed.has('fontFamily')) &&
    paintsText(el)
  ) {
    return true;
  }

  return false;
};

/* ────────────────────────────────────────────────────────────────────────────
   Naming what was found
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * Operator vocabulary for an element, DERIVED rather than authored.
 *
 * The previous shape got its labels from the same hand-written list that decided
 * membership, so the labels drifted with it and a surface nobody had listed was not merely
 * mislabelled, it was invisible. Splitting the two means the worst a stale classifier can
 * do is call something "Other elements" — a wrong word, never a missing count.
 *
 * Order matters: the first rule that matches wins, so the specific cases come before the
 * containers that would otherwise swallow them.
 */
const LABEL_RULES: { label: string; selector: string }[] = [
  { label: 'The scan animation', selector: '[data-vera-scan-pulse]' },
  { label: 'Buttons', selector: '.MuiButton-root, button, [role="button"]' },
  { label: 'Fields', selector: '.MuiInputBase-root, input, textarea, select' },
  {
    label: 'Headings',
    selector:
      '.MuiTypography-h1, .MuiTypography-h2, .MuiTypography-h3, .MuiTypography-h4, h1, h2, h3, h4',
  },
  { label: 'Dividers', selector: '.MuiDivider-root, hr' },
  { label: 'Menu and picker rows', selector: '.MuiMenuItem-root, [role="menuitem"], [role="option"]' },
  { label: 'Page-number pills', selector: '.MuiPaginationItem-root' },
  { label: 'Chips and badges', selector: '.MuiChip-root, .MuiBadge-badge' },
  { label: 'Switches and checkboxes', selector: '.MuiSwitch-root, .MuiCheckbox-root, .MuiRadio-root' },
  { label: 'Icons', selector: 'svg, path, circle, rect[fill], .MuiSvgIcon-root' },
  { label: 'Images and avatars', selector: 'img, .MuiAvatar-root' },
  { label: 'Links', selector: 'a, .MuiLink-root' },
  { label: 'Tabs', selector: '.MuiTab-root, [role="tab"]' },
  { label: 'Tooltips', selector: '.MuiTooltip-tooltip' },
  { label: 'Sheets and drawers', selector: '.MuiDrawer-paper, .MuiDialog-paper, .MuiModal-root' },
  { label: 'Cards and panels', selector: '.MuiPaper-root, .MuiCard-root' },
  { label: 'Text', selector: '.MuiTypography-root, p, span, li, label, td, th' },
];

const labelFor = (el: Element, isPageSurface: boolean): string => {
  if (isPageSurface) return 'The page surface';
  for (const rule of LABEL_RULES) {
    try {
      if (el.matches(rule.selector)) return rule.label;
    } catch {
      // A selector this browser cannot parse is an authoring bug; skip the rule rather
      // than losing the element to an exception.
    }
  }
  return 'Other elements';
};

/* ────────────────────────────────────────────────────────────────────────────
   Marks
   ──────────────────────────────────────────────────────────────────────────── */

const STYLE_ID = 'vera-studio-token-highlight';
const TRANSITION_CANCEL_ID = 'vera-studio-transition-cancel';
const MARK_ATTR = 'data-vera-token-highlight';

/**
 * Cancel every CSS transition the probe just armed, before the task ends.
 *
 * ## What this is fixing
 *
 * The probe changes computed values and then reads them back, and a read forces a style
 * recalc — which is precisely the moment the browser CREATES a transition for any property
 * whose value moved. `flushSync` stops the perturbed tree from being PAINTED, and that part
 * of the docblock above was right; it does nothing about time-based animations the task
 * started, which play out over the frames AFTER it ends. Measured on the certificate flow:
 * 48 visible elements transition a property the probe moves, led by
 * `MuiIconButton-sizeSmall` at `background-color 0.15s` and `MuiSvgIcon` at `fill 0.2s` —
 * the photo controls Spencer reported flashing.
 *
 * ## Why this CANCELS afterwards instead of suppressing throughout, which was tried first
 *
 * The obvious shape is to hold `transition: none` across the whole probe. It kills the flash
 * and it also CHANGES THE MEASUREMENT, which disqualifies it. Measured on the certificates
 * flow at a fixed 3,397-element tree, interleaved four times so DOM growth could not explain
 * it: `text_color` outlined 13 elements unfrozen and 29 frozen, every run. Checked against a
 * gold standard of two REAL async renders with transitions allowed to finish, all 16 of the
 * extra elements were absent from the real changed set — the suppressed-throughout version
 * was inventing false positives, in a feature whose entire purpose is not to assert things
 * it has not verified. (The mechanism was not chased down; that it perturbs the numbers is
 * disqualifying on its own.)
 *
 * Cancelling afterwards has neither problem, and the reason it is sufficient is that
 * `measureTokenHighlight` is ONE SYNCHRONOUS TASK: every probe render is `flushSync` and
 * every snapshot is a synchronous read, so no frame can occur between a transition being
 * armed and this running. A transition that is created and cancelled inside a single task
 * never gets a chance to paint. The measurement sees exactly the styles it saw before this
 * function existed — verified at 13, matching the unfrozen number and the gold standard.
 *
 * ## The ordering
 *
 * 1. **Append `transition: none`, then flush.** Removing a property from `transition-property`
 *    cancels any running transition on it, and the flush is what makes the browser act on
 *    that now rather than at its next natural recalc. Elements land on their restored values
 *    because the real theme has already been put back by this point.
 * 2. **Remove the sheet, after a short tail.** Nothing re-arms from the probe itself: the
 *    flush in step 1 committed the restored values, so when `transition` comes back there is
 *    no pending value change to transition FROM. Dropping step 1's flush is what would
 *    reintroduce the flash with the fix apparently in place.
 *
 *    The TAIL is VRG-779, and it covers a case step 1 structurally cannot. Step 1 cancels
 *    transitions that are armed BY THE TIME IT RUNS. A transition armed afterwards — by a
 *    style restoration that lands in a LATER task rather than inside the probe's synchronous
 *    window — has nothing to cancel it, and plays in full.
 *
 *    Found by Spencer on the carousel's page dots: clicking "What does this change?" made the
 *    current-page pill collapse to a 6px circle and grow back. Reproduced by sampling the dot
 *    row immediately after the click (all four dots round and equal) and again moments later
 *    (the pill restored). The dot carries `transition: width .26s`, and `.selected` is what
 *    makes it 18px wide, so anything that drops and restores that rule animates the restore
 *    rather than snapping it. This is NOT a remount — the probe's own measurement found none,
 *    and that still holds; it is the emotion/JSS `<style>` churn the docblock above already
 *    records as the only node change across a probe.
 *
 *    Holding the sheet for a window AFTER the probe is categorically different from holding
 *    it ACROSS the probe, which was measured and rejected above for moving the numbers: by
 *    this point every measurement is finished and the real theme is already back, so there is
 *    nothing left to distort. The only cost is that a transition genuinely started by the
 *    operator during the tail — a hover, say — snaps instead of easing, once, for a fraction
 *    of a second, on a surface they are looking at rather than interacting with.
 *
 * Called after the ring is applied as well as after the probe, so elements that transition
 * `box-shadow` (`MuiButton-contained`, `MuiPaper-elevation16`) do not fade their outline in
 * over 0.25–0.3s while every other ring snaps on.
 *
 * `transition` only. `animation` is deliberately NOT touched: the scan pulse is
 * `veraScanPulse 3s infinite` and the reveal is `both`-filled, so cancelling and restoring
 * `animation` would restart them from zero — trading a fixed flash for a new one. Nothing
 * arms an animation here anyway; measured across a probe, no element is remounted and no
 * `animation-name` changes.
 *
 * `!important` for the reason documented throughout this app: `index.css`'s
 * `#vera-shadow-root *` carries ID specificity. Verified in a real tree — 67 elements with a
 * live transition read `0s` while the sheet is present and `0.3s`/`0.2s`/`0.15s` again once
 * it is gone.
 *
 * `document.body.offsetWidth` is the flush: it forces layout, and layout forces style
 * recalc, for the whole document including shadow trees.
 */
/**
 * How long the suppression sheet stays attached after the probe (VRG-779).
 *
 * 600ms, and the number is empirical rather than derived: it is comfortably past the
 * longest transition this module measured anywhere in the preview flow (`MuiSwitch-track`
 * at 0.5s) and past the dot's own `width .26s`, so a restoration landing any time inside
 * the tail finds the sheet still in place and snaps instead of easing.
 *
 * `setTimeout`, NOT `requestAnimationFrame`: rAF does not fire reliably in the Studio
 * preview pane, so a rAF-scheduled removal can simply never run and leave the sheet
 * attached for the rest of the session — transitions dead everywhere, with nothing
 * pointing at why.
 */
const TRANSITION_CANCEL_TAIL_MS = 600;

/** The pending tail removal, so overlapping probes don't strand a sheet. */
let transitionCancelTail: ReturnType<typeof setTimeout> | undefined;

const cancelArmedTransitions = (root: ShadowRoot | null): void => {
  /*
   * A probe during the previous tail would otherwise append a second sheet and leak the
   * first — the old timer's `remove()` is bound to a node this call is about to replace.
   * Clear the timer and drop any sheet still attached before appending a fresh one.
   */
  if (transitionCancelTail !== undefined) {
    clearTimeout(transitionCancelTail);
    transitionCancelTail = undefined;
  }
  (root ?? document).querySelector(`#${TRANSITION_CANCEL_ID}`)?.remove();

  const style = document.createElement('style');
  style.id = TRANSITION_CANCEL_ID;
  style.textContent = `*, *::before, *::after { transition: none !important; }`;
  (root ?? document.head).appendChild(style);
  void document.body.offsetWidth;

  transitionCancelTail = setTimeout(() => {
    transitionCancelTail = undefined;
    /*
     * Flush again before letting transitions back. Whatever the tail was covering has
     * settled by now; committing those values while the sheet is still attached means
     * there is no pending change left for the browser to animate when it is removed —
     * the same reasoning as step 1, applied to the end of the window instead of its start.
     */
    void document.body.offsetWidth;
    style.remove();
  }, TRANSITION_CANCEL_TAIL_MS);
};

/**
 * The annotation treatment: the PRODUCT TOUR's spotlight ring, from `@repo/ui`.
 *
 * It used to be a dashed indigo of this feature's own invention, and Spencer's note on
 * seeing it was that there should be one treatment, not two: *"just want to use the same
 * tour spotlight — not the dotted or hashed line spotlights."* Two different ways of
 * saying "look at this" in one product read as two different meanings.
 *
 * The values come from `packages/ui/theme/spotlight.ts`, which the tour also reads, so
 * this is one source rather than a copy that will drift. It must NOT come from this app's
 * theme: `apps/app` derives its whole `brand` ramp from the tenant's `primary_color`, so a
 * theme-sourced ring would be drawn in the very colour the operator is changing, and would
 * disappear on any brand whose purple is near Vera's.
 *
 * Two deliberate differences from the tour, both forced by highlighting a SET:
 *
 * - **No dim.** The tour's ring sits in a hole cut out of a 55%-black scrim, which is what
 *   makes a single anchor pop. Neither half of that survives here. A scrim over the
 *   preview would tint every colour on it, and judging colour is precisely what the
 *   operator is doing. And with 52 holes it is not a spotlight, it is a stencil.
 * - **`contrastHairline`.** Without the dim the ring has to carry its own contrast, since
 *   a dark `bg_color` puts Vera purple on near-black.
 *
 * `outline`, not `border` — a border participates in layout, so switching it on would
 * shift the very page being judged.
 *
 * `!important` throughout, for the reason documented four times over in this app:
 * `index.css`'s `#vera-shadow-root *` carries ID specificity and beats the single class
 * emotion writes for `sx`.
 */
const highlightCss = (ring: string, minRadius: number): string =>
  [
    `[${MARK_ATTR}] {\n  ${ring}\n}`,
    /*
      The corner floor, applied only to elements the marking pass tagged `round` — those
      whose own corners are already square. See `SPOTLIGHT_PREVIEW_MIN_RADIUS` for why this
      may only ever raise a radius and why it is suppressed for `border_radius`.
    */
    `[${MARK_ATTR}="round"] {\n  border-radius: ${minRadius}px !important;\n}`,
  ].join('\n');

/** Remove the marks and the rule. Safe to call when nothing is highlighted. */
export const clearTokenHighlight = (root: ShadowRoot | null): void => {
  const scope = scopeOf(root);
  scope.querySelectorAll?.(`[${MARK_ATTR}]`).forEach(el => el.removeAttribute(MARK_ATTR));
  (scope as ParentNode).querySelector?.(`#${STYLE_ID}`)?.remove();
};

/* ────────────────────────────────────────────────────────────────────────────
   The measurement
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * Outline everything `token` actually changes on this flow, and report what matched.
 *
 * @param renderWithTheme MUST render SYNCHRONOUSLY — `flushSync`, not `setState`. The
 * whole probe runs inside one task so the browser never paints a perturbed tree; an
 * asynchronous render would both flash the sentinel values on screen and make every
 * snapshot read the tree from before the change it is meant to measure.
 *
 * @param ringCss the spotlight declaration block, passed in rather than imported so this
 * module stays free of `@repo/ui`'s module graph — it runs inside a shadow root in an
 * iframe, and the caller already holds the dependency.
 *
 * @param minCornerRadius the corner floor for square-cornered elements, passed in for the
 * same reason as `ringCss`. See `SPOTLIGHT_PREVIEW_MIN_RADIUS`.
 */
export const measureTokenHighlight = (
  root: ShadowRoot | null,
  token: StudioTokenName | null,
  theme: HighlightTheme | null,
  renderWithTheme: (theme: HighlightTheme) => void,
  ringCss: string,
  minCornerRadius: number,
): HighlightMeasurement => {
  clearTokenHighlight(root);
  if (!token || !theme) return { parts: [], invisible: 0 };

  const props = propsFor(token);
  const probes = probeThemes(token, theme);
  if (probes.length === 0) return { parts: [], invisible: 0 };

  /** element -> the properties that moved under at least one probe. */
  const changed = new Map<Element, Set<string>>();

  /*
    Everything from here to the return happens inside one synchronous task, and the last
    thing it does is cancel the transitions that task armed — the probe's AND the ring's.
    See `cancelArmedTransitions` for why cancelling afterwards is correct and suppressing
    throughout is not. `finally` rather than a call before the return: a throw partway
    through would otherwise leave a probe's transitions live and flash the sentinel colours
    on screen, which is the exact bug this exists to prevent.
  */
  try {

  let base: Map<Element, string[]>;
  try {
    /*
      COMPARE LIKE WITH LIKE: the baseline is measured after a synchronous re-render of the
      SAME theme, not from the tree as it happens to be sitting.

      This looks redundant and is not. Measured on the certificate flow: the carousel's page
      dots read `box-shadow: none` in the settled tree and the library's own
      `rgba(0,0,0,0.9) 1px 1px 2px` in every `flushSync`-rendered tree — emotion's override
      of that rule is transiently absent from the CSSOM when a re-render is forced inside a
      single task. Diffing a settled baseline against a forced probe therefore reported four
      carousel dots as governed by `secondary_color`, which they are not: a real
      #0057B8 -> #ff00ff render moves nothing about them.

      The artefact is a property of HOW the tree was rendered, not of the token, so it
      cancels the moment both sides are rendered the same way. Re-rendering the live theme
      first is what makes that true. A fresh object is required — React bails out of a
      `setState` with an identical reference, and the bail-out would silently restore the
      bug.
    */
    renderWithTheme({ ...theme });
    base = snapshot(allElements(root), props);

    for (const probe of probes) {
      renderWithTheme(probe);
      /*
        Re-read the element list rather than reusing `elements`. A theme change should not
        restructure the tree, but "should not" is not a guarantee worth a crash: a node
        React replaced is simply absent from this pass and contributes nothing, instead of
        throwing on a detached node.
      */
      const after = snapshot(allElements(root), props);
      for (const [el, values] of after) {
        const before = base.get(el);
        if (!before) continue;
        for (let i = 0; i < props.length; i += 1) {
          if (before[i] !== values[i]) {
            const set = changed.get(el) ?? new Set<string>();
            set.add(props[i] as string);
            changed.set(el, set);
          }
        }
      }
    }
  } finally {
    // Restore unconditionally. A throw mid-probe that left the operator's preview showing
    // magenta would be a far worse bug than the one that caused it.
    renderWithTheme(theme);
  }

  const visible: Element[] = [];
  let invisible = 0;
  for (const [el, moved] of changed) {
    if (el.isConnected && paintsChange(el, moved)) visible.push(el);
    else invisible += 1;
  }

  /*
    Collapse a run of nested elements that all moved the SAME way into its outermost
    member.

    Only backgrounds do this in practice: a page surface and the container painted the same
    colour on top of it both change, and outlining both draws two rings 1px apart that say
    nothing the outer one did not. Restricted to the case where the descendant's changed
    properties are a subset of the ancestor's, so a card that changed its background AND
    its border is never swallowed by the page behind it.
  */
  const visibleSet = new Set(visible);
  const outlined = visible.filter(el => {
    const own = changed.get(el);
    if (!own || own.size !== 1 || !own.has('backgroundColor')) return true;
    for (let p = el.parentElement; p; p = p.parentElement) {
      if (!visibleSet.has(p)) continue;
      const theirs = changed.get(p);
      if (theirs?.has('backgroundColor')) return false;
    }
    return true;
  });

  /*
    The outermost outlined element gets called "the page surface" rather than whatever its
    tag suggests, because that is what the operator is looking at. Computed by depth so it
    does not depend on document order.
  */
  const depth = (el: Element): number => {
    let n = 0;
    for (let p = el.parentElement; p; p = p.parentElement) n += 1;
    return n;
  };
  const shallowest = outlined.reduce<Element | null>(
    (best, el) => (best === null || depth(el) < depth(best) ? el : best),
    null,
  );
  const surfaceIsPage =
    shallowest !== null && changed.get(shallowest)?.has('backgroundColor') === true;

  /*
    Should this element's corners be raised to the floor so its ring is not a hard
    rectangle? Only when they are ALREADY square — the floor may never round down a pill or
    a card (see `SPOTLIGHT_PREVIEW_MIN_RADIUS`).

    Two exclusions beyond that:
    - `border_radius`, because rounding corners to annotate the corner-radius control would
      put the annotation in direct contradiction with the thing being annotated.
    - the page surface, where a 4px radius would pull the preview's own corners away from
      the device frame around it — the one place the change reads as a restyle rather than
      as a softened ring.
  */
  const shouldRound = (el: Element): boolean => {
    if (token === 'border_radius') return false;
    if (surfaceIsPage && el === shallowest) return false;
    const c = window.getComputedStyle(el) as unknown as Record<string, string>;
    return RADIUS_PROPS.every(p => {
      const v = c[p] ?? '0px';
      return parseFloat(v) === 0;
    });
  };

  const byLabel = new Map<string, number>();
  for (const el of outlined) {
    el.setAttribute(MARK_ATTR, shouldRound(el) ? 'round' : '');
    const label = labelFor(el, surfaceIsPage && el === shallowest);
    byLabel.set(label, (byLabel.get(label) ?? 0) + 1);
  }

  if (outlined.length > 0) {
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = highlightCss(ringCss, minCornerRadius);
    (root ?? document.head).appendChild(style);
  }

  /*
    Biggest group first. The operator is scanning for "what is the main thing this
    touches", and an alphabetical or insertion order buries a 40 under a 1.
  */
  const parts = Array.from(byLabel, ([label, count]) => ({ label, count })).sort(
    (a, b) => b.count - a.count,
  );

  return { parts, invisible };

  } finally {
    cancelArmedTransitions(root);
  }
};
